import { Component, OnInit } from '@angular/core';
import { LanguageService } from '../../services/language.service';
import { EServiceCategoryService } from '../e-service-category/e-service-category.service';
import { EServiceRequest } from './e-service.model';
import { EServiceService } from './e-service.service';

@Component({
  selector: 'app-e-service',
  standalone: false,
  templateUrl: './e-service.component.html',
  styleUrls: ['./e-service.component.css']
})
export class EServiceComponent implements OnInit {

  successMessage = '';
  errorMessage = '';

  languages: any[] = [];
  services: any[] = [];
  filteredServices: any[] = [];
  categories: any[] = [];

  currentPage = 1;
  pageSize = 6;

  activeTab = 'all';
  activeTabPopup = 1;
  searchText = '';

  isModalOpen = false;
  isEnableSave = false;
  isEnableUpdate = false;
  isEnableDelete = false;

  serviceId = 0;
  categoryId: number | null = null;
  icon = '';
  priceFrom: number | null = null;
  priceTo: number | null = null;
  durationDays: number | null = null;
  applyUrl = '';
  downloadUrl = '';
  requirementUrl = '';
  isEnabled = true;
  isFronted = false;

  titleKh = '';
  subtitleKh = '';
  descriptionKh = '';
  linkTextKh = '';
  titleEn = '';
  subtitleEn = '';
  descriptionEn = '';
  linkTextEn = '';

  constructor(
    private eServiceService: EServiceService,
    private categoryService: EServiceCategoryService,
    private languageService: LanguageService
  ) { }

  ngOnInit(): void {
    this.loadLanguages();
    this.loadCategories();
    this.loadServices('');
  }

  loadLanguages(): void {
    this.languageService.getAll().subscribe({
      next: (res: any) => {
        this.languages = (res.data || []).sort((a: any, b: any) => a.languageId - b.languageId);

        if (this.languages.length) {
          this.setTab(this.languages[0].code);
          this.setTabPopup(this.languages[0].languageId);
        }
      },
      error: () => this.showError('Failed to load languages.')
    });
  }

  loadCategories(): void {
    this.categoryService.getAll('').subscribe({
      next: (res: any) => {
        this.categories = (res.data || []).map((item: any) => ({
          categoryId: item.id,
          name:
            item.translations?.find((t: any) => t.languageCode === 'en')?.name ||
            item.translations?.[0]?.name ||
            'Unknown'
        }));
      },
      error: () => this.showError('Failed to load e-service categories.')
    });
  }

  loadServices(langCode: string): void {
    this.eServiceService.getAll(langCode).subscribe({
      next: (res: any) => {
        const apiData = res.data || [];

        this.services = apiData
          .filter((item: any) => item.translations?.length)
          .flatMap((item: any) => {
            const categoryId = item.categories?.[0]?.eRegistrationCategoryId;
            const categoryName =
              item.categories?.find((c: any, index: number) =>
                item.translations?.[index]?.languageCode === 'en'
              )?.name ||
              item.categories?.[0]?.name ||
              'Unknown';

            return item.translations.map((t: any) => ({
              serviceId: item.eRegistrationServiceId,
              categoryId,
              categoryName,
              icon: item.icon,
              priceFrom: item.priceFrom,
              priceTo: item.priceTo,
              durationDays: item.durationDays,
              applyUrl: item.applyUrl,
              downloadUrl: item.downloadUrl,
              requirementUrl: item.requirementUrl,
              isEnabled: item.isEnabled,
              isFronted: item.isFronted,
              languageCode: t.languageCode,
              languageId: t.languageId,
              title: t.title,
              subtitle: t.subtitle,
              description: t.description,
              linkText: t.linkText
            }));
          });

        this.filterServices(this.activeTab);
      },
      error: () => this.showError('Failed to load e-services.')
    });
  }

  setTab(tab: string): void {
    this.activeTab = tab;
    this.currentPage = 1;
    this.filterServices(tab);
  }

  setTabPopup(languageId: number): void {
    this.activeTabPopup = languageId;
  }

  filterServices(tab: string): void {
    const keyword = this.searchText.trim().toLowerCase();
    this.currentPage = 1;

    this.filteredServices = this.services.filter(service => {
      const status = service.isEnabled ? 'Active' : 'Inactive';
      const frontStatus = service.isFronted ? 'Front' : 'Hidden';
      const matchesTab = tab === 'all' || service.languageCode === tab;
      const matchesSearch = !keyword ||
        `${service.categoryName ?? ''} ${service.title ?? ''} ${service.subtitle ?? ''} ${service.description ?? ''} ${service.linkText ?? ''} ${service.applyUrl ?? ''} ${service.downloadUrl ?? ''} ${service.requirementUrl ?? ''} ${service.priceFrom ?? ''} ${service.priceTo ?? ''} ${service.durationDays ?? ''} ${status} ${frontStatus} ${service.languageCode ?? ''}`
          .toLowerCase()
          .includes(keyword);

      return matchesTab && matchesSearch;
    });
  }

  getLanguageCount(code: string): number {
    return this.services.filter(s => s.languageCode === code).length;
  }

  get pagedServices(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredServices.slice(start, start + this.pageSize);
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredServices.length / this.pageSize));
  }

  changePage(page: number): void {
    this.currentPage = page;
  }

  addNew(): void {
    this.resetForm();
    this.openModal('Add');
  }

  viewData(id: number, mode: 'Edit' | 'Delete'): void {
    this.eServiceService.getById(id, '').subscribe({
      next: (res: any) => {
        const data = res.data;

        if (!data) {
          this.showError('E-service not found.');
          return;
        }

        this.serviceId = data.eRegistrationServiceId;
        this.categoryId = data.categories?.[0]?.eRegistrationCategoryId || null;
        this.icon = data.icon || '';
        this.priceFrom = data.priceFrom ?? null;
        this.priceTo = data.priceTo ?? null;
        this.durationDays = data.durationDays ?? null;
        this.applyUrl = data.applyUrl || '';
        this.downloadUrl = data.downloadUrl || '';
        this.requirementUrl = data.requirementUrl || '';
        this.isEnabled = data.isEnabled;
        this.isFronted = data.isFronted;

        this.titleKh = '';
        this.subtitleKh = '';
        this.descriptionKh = '';
        this.linkTextKh = '';
        this.titleEn = '';
        this.subtitleEn = '';
        this.descriptionEn = '';
        this.linkTextEn = '';

        data.translations.forEach((t: any) => {
          if (t.languageCode === 'kh') {
            this.titleKh = t.title;
            this.subtitleKh = t.subtitle || '';
            this.descriptionKh = t.description || '';
            this.linkTextKh = t.linkText || '';
          }

          if (t.languageCode === 'en') {
            this.titleEn = t.title;
            this.subtitleEn = t.subtitle || '';
            this.descriptionEn = t.description || '';
            this.linkTextEn = t.linkText || '';
          }
        });

        this.activeTabPopup = this.activeTab === 'kh' ? 1 : 2;
        this.openModal(mode);
      },
      error: () => this.showError('Unable to load e-service.')
    });
  }

  save(): void {
    if (!this.validateForm()) {
      return;
    }

    this.eServiceService.create(this.buildPayload()).subscribe({
      next: () => {
        this.showSuccess('E-service created successfully.');
        this.afterSuccess();
      },
      error: () => this.showError('Failed to create e-service.')
    });
  }

  update(): void {
    if (!this.validateForm()) {
      return;
    }

    this.eServiceService.update(this.serviceId, this.buildPayload()).subscribe({
      next: () => {
        this.showSuccess('E-service updated successfully.');
        this.afterSuccess();
      },
      error: () => this.showError('Failed to update e-service.')
    });
  }

  delete(): void {
    if (!this.serviceId) {
      this.showError('Please select an e-service.');
      return;
    }

    if (!confirm('Are you sure you want to delete this e-service?')) {
      return;
    }

    this.eServiceService.delete(this.serviceId).subscribe({
      next: () => {
        this.showSuccess('E-service deleted successfully.');
        this.afterSuccess();
      },
      error: () => this.showError('Failed to delete e-service.')
    });
  }

  openModal(eventName?: string): void {
    this.visibleButtonControl(eventName || '');
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
  }

  visibleButtonControl(eventName: string): void {
    this.isEnableSave = eventName === 'Add';
    this.isEnableUpdate = eventName === 'Edit';
    this.isEnableDelete = eventName === 'Delete';
  }

  private buildPayload(): EServiceRequest {
    return {
      eRegistrationCategoryId: this.categoryId!,
      icon: this.icon,
      priceFrom: this.priceFrom,
      priceTo: this.priceTo,
      durationDays: this.durationDays,
      applyUrl: this.applyUrl,
      downloadUrl: this.downloadUrl,
      requirementUrl: this.requirementUrl,
      isEnabled: this.isEnabled,
      isFronted: this.isFronted,
      translations: [
        {
          languageId: 1,
          linkText: this.linkTextKh,
          title: this.titleKh,
          subtitle: this.subtitleKh,
          description: this.descriptionKh
        },
        {
          languageId: 2,
          linkText: this.linkTextEn,
          title: this.titleEn,
          subtitle: this.subtitleEn,
          description: this.descriptionEn
        }
      ]
    };
  }

  private validateForm(): boolean {
    if (!this.categoryId) {
      this.showError('Please choose an e-service category.');
      return false;
    }

    if (!this.titleKh.trim() || !this.titleEn.trim()) {
      this.showError('Please enter service titles for both languages.');
      return false;
    }

    return true;
  }

  private afterSuccess(): void {
    this.closeModal();
    this.resetForm();
    this.loadServices('');
  }

  private resetForm(): void {
    this.serviceId = 0;
    this.categoryId = null;
    this.icon = '';
    this.priceFrom = null;
    this.priceTo = null;
    this.durationDays = null;
    this.applyUrl = '';
    this.downloadUrl = '';
    this.requirementUrl = '';
    this.isEnabled = true;
    this.isFronted = false;
    this.titleKh = '';
    this.subtitleKh = '';
    this.descriptionKh = '';
    this.linkTextKh = '';
    this.titleEn = '';
    this.subtitleEn = '';
    this.descriptionEn = '';
    this.linkTextEn = '';
    this.activeTabPopup = this.languages[0]?.languageId || 1;
  }

  private showSuccess(message: string): void {
    this.successMessage = message;
    setTimeout(() => this.clearMessages(), 3000);
  }

  private showError(message: string): void {
    this.errorMessage = message;
    setTimeout(() => this.clearMessages(), 3000);
  }

  private clearMessages(): void {
    this.successMessage = '';
    this.errorMessage = '';
  }

}
